/* ==========================================================================
   LUK Bundle Builder
   - Lets a shopper pick N products from a collection for a fixed deal price.
   - Selection is kept in sessionStorage; the cart is NOT touched until the
     shopper presses "Place order", so Dawn's cart drawer never opens.
   - On "Place order" all selected lines are added via /cart/add.js in one
     request and the shopper is redirected straight to checkout.
   ========================================================================== */
(function () {
  'use strict';

  // Build a money formatter from a sample rendered by Liquid (e.g. "Dhs. 1,234.56"),
  // so JS output matches the store's own money format in every market.
  function makeMoneyFormatter(sample, currency, hideZeroCents) {
    const m = sample && sample.match(/1(\D?)234(?:(\D)56)?/);
    if (!m) {
      return (cents) => currency + ' ' + (cents / 100).toFixed(2);
    }
    const thousands = m[1] || '';
    const decimal = m[2] || '';
    return function (cents) {
      const abs = Math.abs(cents);
      const whole = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const frac = (abs % 100).toString().padStart(2, '0');
      const num = decimal && !(hideZeroCents && frac === '00') ? whole + decimal + frac : whole;
      return (cents < 0 ? '-' : '') + sample.replace(m[0], num);
    };
  }

  class LukBundle {
    constructor(root) {
      this.root = root;
      this.size = parseInt(root.dataset.bundleSize, 10) || 3;
      this.currency = root.dataset.currency || 'AED';
      this.locale = root.dataset.locale || 'en';
      this.storageKey = 'luk-bundle:' + (root.dataset.storageKey || 'default');
      this.cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
      this.checkoutUrl = root.dataset.checkoutUrl || '/checkout';
      this.cartUrl = root.dataset.cartUrl || '/cart';
      this.boxSaving = parseInt(root.dataset.boxSavingCents, 10) || 0;
      this.discountCode = (root.dataset.discountCode || '').trim();
      this.strings = JSON.parse(root.dataset.strings || '{}');
      this.money = makeMoneyFormatter(root.dataset.moneySample, this.currency, root.dataset.hideZeroCents === 'true');

      // Deal price is configured in the shop currency; convert for markets.
      const shopCents = parseInt(root.dataset.bundlePriceCents, 10) || 0;
      const rate =
        window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
          ? parseFloat(window.Shopify.currency.rate)
          : 1;
      this.bundleCents = Math.round(shopCents * (isFinite(rate) && rate > 0 ? rate : 1));

      this.cards = Array.from(root.querySelectorAll('[data-luk-card]'));
      this.products = {};
      this.cards.forEach((card) => {
        this.products[card.dataset.variantId] = {
          id: card.dataset.variantId,
          title: card.dataset.title,
          image: card.dataset.image,
          price: parseInt(card.dataset.price, 10) || 0,
          available: card.dataset.available === 'true',
          card: card
        };
      });

      const avail = Object.values(this.products).filter((p) => p.available && p.price > 0);
      this.avgPrice = avail.length ? avail.reduce((a, p) => a + p.price, 0) / avail.length : 0;
      this.bar = root.querySelector('[data-luk-bar]');
      this.toast = root.querySelector('[data-luk-toast]');
      this.state = this.load();
      this.applyDeepLink();
      this.bind();
      this.render();
      document.body.classList.add('luk-bar-active');
    }

    /* ---------- shareable prefilled box: ?box=<variantId>,<variantId>,... ---------- */
    applyDeepLink() {
      let raw;
      try {
        raw = new URLSearchParams(window.location.search).get('box');
      } catch (e) {
        return;
      }
      if (!raw) return;
      const next = {};
      let total = 0;
      raw.split(',').forEach((id) => {
        const key = id.trim();
        if (!key || !this.products[key] || !this.products[key].available) return;
        if (total >= this.size) return;
        next[key] = (next[key] || 0) + 1;
        total += 1;
      });
      if (total > 0) {
        this.state = next;
        this.save();
      }
    }

    /* ---------- persistence ---------- */
    load() {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        const clean = {};
        Object.keys(parsed).forEach((id) => {
          if (this.products[id] && this.products[id].available && parsed[id] > 0) clean[id] = parsed[id];
        });
        // never exceed the bundle size after a reload
        let total = 0;
        Object.keys(clean).forEach((id) => {
          const allowed = Math.max(0, Math.min(clean[id], this.size - total));
          if (allowed === 0) delete clean[id];
          else clean[id] = allowed;
          total += allowed;
        });
        return clean;
      } catch (e) {
        return {};
      }
    }
    save() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (e) {
        /* storage unavailable: selection just won't persist */
      }
    }

    /* ---------- state ---------- */
    total() {
      return Object.values(this.state).reduce((a, b) => a + b, 0);
    }
    subtotal() {
      return Object.keys(this.state).reduce((sum, id) => sum + this.products[id].price * this.state[id], 0);
    }
    add(id) {
      // No ceiling: every completed set of `size` is another discounted box.
      if (!this.products[id] || !this.products[id].available) return;
      this.state[id] = (this.state[id] || 0) + 1;
      this.commit();
    }
    remove(id, all) {
      if (!this.state[id]) return;
      if (all || this.state[id] <= 1) delete this.state[id];
      else this.state[id] -= 1;
      this.commit();
    }
    commit() {
      this.save();
      this.render();
    }

    /* ---------- events ---------- */
    bind() {
      this.root.addEventListener('click', (evt) => {
        const btn = evt.target.closest('[data-luk-action]');
        if (!btn) return;
        const action = btn.dataset.lukAction;
        const id = btn.dataset.variantId;
        switch (action) {
          case 'add':
          case 'inc':
            evt.preventDefault();
            this.add(id);
            break;
          case 'dec':
            evt.preventDefault();
            this.remove(id, false);
            break;
          case 'remove':
            evt.preventDefault();
            this.remove(id, true);
            break;
          case 'toggle': {
            evt.preventDefault();
            const open = this.bar.classList.toggle('is-open');
            const t = this.bar.querySelector('[data-luk-action="toggle"]');
            if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
            break;
          }
          case 'checkout':
            evt.preventDefault();
            this.checkout();
            break;
        }
      });
    }

    /* ---------- rendering ---------- */
    render() {
      const total = this.total();
      // Sets stack: every `size` perfumes is another discounted box, and the
      // shopper can keep going. Leftovers stay at their own price.
      const boxes = Math.floor(total / this.size);
      const inSet = total % this.size;
      const complete = boxes > 0;

      // cards — no cap, the box grows as long as the shopper wants
      this.cards.forEach((card) => {
        const id = card.dataset.variantId;
        const qty = this.state[id] || 0;
        card.classList.toggle('is-selected', qty > 0);
        const val = card.querySelector('[data-luk-qty]');
        if (val) val.textContent = qty;
      });

      // bar
      if (!this.bar) return;
      this.bar.classList.toggle('is-complete', complete);

      const slots = this.bar.querySelectorAll('[data-luk-slot]');
      const filled = [];
      Object.keys(this.state).forEach((id) => {
        for (let i = 0; i < this.state[id]; i++) filled.push(this.products[id]);
      });
      // The slots track the set being filled right now, not the whole box.
      const offset = boxes * this.size;
      slots.forEach((slot, i) => {
        const p = inSet === 0 && boxes > 0 ? filled[offset - this.size + i] : filled[offset + i];
        slot.classList.toggle('is-filled', !!p);
        slot.innerHTML = p ? '<img src="' + p.image + '" alt="" loading="lazy">' : '';
      });

      const count = this.bar.querySelector('[data-luk-count]');
      if (count) {
        count.textContent = (this.strings.count || '[count]/[size] Added')
          .replace('[count]', total)
          .replace('[size]', this.size);
      }
      const boxesEl = this.bar.querySelector('[data-luk-boxes]');
      if (boxesEl) {
        boxesEl.hidden = boxes < 1;
        boxesEl.textContent = boxes === 1 ? '1 box' : boxes + ' boxes';
      }

      // How many more to complete the set in progress.
      const missing = inSet === 0 ? this.size : this.size - inSet;
      const price = this.money(this.bundleCents);
      const estSaving = this.boxSaving || Math.max(0, Math.round(this.avgPrice * this.size) - this.bundleCents);
      const fill = (tpl) =>
        (tpl || '')
          .replace('[price]', price)
          .replace('[size]', this.size)
          .replace('[n]', missing)
          .replace('[saving]', this.money(estSaving));

      const hint = this.bar.querySelector('[data-luk-hint]');
      if (hint) {
        if (total === 0) hint.textContent = fill(this.strings.start);
        else if (inSet === 0) hint.textContent = fill(this.strings.unlocked);
        else hint.textContent = fill(this.strings.progress);
      }
      const progress = this.bar.querySelector('[data-luk-progress]');
      if (progress) {
        const pct = inSet === 0 && total > 0 ? 100 : (inSet / this.size) * 100;
        progress.style.width = pct + '%';
      }

      const sub = this.subtotal();
      const saving = boxes * this.boxSaving;
      const deal = Math.max(0, sub - saving);

      const savingEl = this.bar.querySelector('[data-luk-saving]');
      if (savingEl) {
        savingEl.hidden = saving <= 0;
        savingEl.textContent = (this.strings.saving || 'Saving: [amount]').replace('[amount]', this.money(saving));
      }
      const dealEl = this.bar.querySelector('[data-luk-deal]');
      if (dealEl) dealEl.textContent = this.money(total === 0 ? this.bundleCents : deal);
      const wasEl = this.bar.querySelector('[data-luk-was]');
      if (wasEl) {
        wasEl.hidden = saving <= 0;
        wasEl.textContent = this.money(sub);
      }

      const cta = this.bar.querySelector('[data-luk-action="checkout"]');
      if (cta) cta.setAttribute('aria-disabled', total > 0 ? 'false' : 'true');

      // expanded list
      const list = this.bar.querySelector('[data-luk-list]');
      if (list) {
        const ids = Object.keys(this.state);
        if (!ids.length) {
          list.innerHTML = '<p class="luk-bar__empty">' + (this.strings.empty || 'No perfumes added yet.') + '</p>';
        } else {
          list.innerHTML = ids
            .map((id) => {
              const p = this.products[id];
              return (
                '<div class="luk-bar__row">' +
                '<img src="' + p.image + '" alt="">' +
                '<span class="luk-bar__row-title">' + p.title + '</span>' +
                '<span class="luk-bar__row-qty">× ' + this.state[id] + '</span>' +
                '<button type="button" class="luk-bar__row-remove" data-luk-action="remove" data-variant-id="' + id + '">' +
                (this.strings.remove || 'Remove') +
                '</button>' +
                '</div>'
              );
            })
            .join('');
        }
      }
    }

    /* ---------- checkout ---------- */
    checkout() {
      const total = this.total();
      if (total < 1) {
        this.notify((this.strings.needMore || 'Add [n] more to unlock the deal').replace('[n]', this.size));
        this.shakeBar();
        const firstCard = this.cards.find((c) => !c.classList.contains('is-selected') && !c.classList.contains('is-soldout'));
        if (firstCard) firstCard.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        return;
      }
      const cta = this.bar.querySelector('[data-luk-action="checkout"]');
      cta.classList.add('is-loading');

      const items = Object.keys(this.state).map((id) => ({ id: parseInt(id, 10), quantity: this.state[id] }));
      const sections = [];
      if (document.getElementById('CartDrawer')) sections.push('cart-drawer', 'cart-icon-bubble');
      if (document.getElementById('main-cart-items')) sections.push('main-cart-items', 'main-cart-footer');

      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items,
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data: data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
          this.state = {};
          try {
            sessionStorage.removeItem(this.storageKey);
          } catch (e) {}
          this.render();
          cta.classList.remove('is-loading');
          if (!this.openDrawer(data)) window.location.href = this.cartUrl;
        })
        .catch((err) => {
          cta.classList.remove('is-loading');
          this.notify(err.message || 'Something went wrong. Please try again.');
        });
    }

    /* Hand the freshly rendered sections to Dawn's drawer and open it. */
    openDrawer(payload) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return false;
      if (!payload || !payload.sections) return false;
      try {
        drawer.renderContents(payload);
      } catch (e) {
        return false;
      }
      requestAnimationFrame(() => {
        if (typeof drawer.open === 'function' && !drawer.classList.contains('active')) drawer.open();
      });
      return true;
    }

    /* ---------- feedback ---------- */
    notify(msg) {
      if (!this.toast) return;
      this.toast.textContent = msg;
      this.toast.classList.add('is-visible');
      clearTimeout(this._t);
      this._t = setTimeout(() => this.toast.classList.remove('is-visible'), 2200);
    }
    shakeBar() {
      if (!this.bar) return;
      this.bar.classList.remove('is-shake');
      void this.bar.offsetWidth;
      this.bar.classList.add('is-shake');
    }
  }

  /* ---------- swipe affordance for the horizontally scrolling rows ---------- */
  const CHEVRON =
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.4"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7.5 4l6 6-6 6"/></svg>';

  function setupSwipeHints() {
    document.querySelectorAll('.luk-group').forEach((group) => {
      const grid = group.querySelector('.luk-grid');
      if (!grid) return;

      let hint = group.querySelector('.luk-swipe');
      if (!hint) {
        hint = document.createElement('button');
        hint.type = 'button';
        hint.className = 'luk-swipe';
        hint.setAttribute('aria-label', 'Show more perfumes');
        hint.innerHTML = CHEVRON;
        hint.addEventListener('click', () => {
          grid.scrollBy({ left: Math.round(grid.clientWidth * 0.7), behavior: 'smooth' });
        });
        group.appendChild(hint);
      }

      const place = () => {
        const scrollable = grid.scrollWidth - grid.clientWidth > 8;
        hint.hidden = !scrollable;
        if (!scrollable) return;
        // Sit against the vertical middle of the product image, not the card.
        // Offsets are measured from the top of the grid, which is itself
        // positioned against the group.
        const media = grid.querySelector('.luk-card__media');
        const centre = media ? media.offsetHeight / 2 : grid.clientHeight / 2;
        const size = hint.offsetHeight || 34;
        hint.style.top = Math.round(grid.offsetTop + centre - size / 2) + 'px';
        const atEnd = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 8;
        hint.classList.toggle('is-end', atEnd);
      };

      place();
      if (!grid.dataset.swipeBound) {
        grid.dataset.swipeBound = '1';
        let ticking = false;
        grid.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            place();
          });
        });
        window.addEventListener('resize', place);
        window.addEventListener('load', place);
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-luk-bundle]').forEach((root) => {
      if (!root.lukBundle) root.lukBundle = new LukBundle(root);
    });
    setupSwipeHints();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Re-init when the theme editor re-renders the section
  document.addEventListener('shopify:section:load', init);
})();
